import { useState, useRef } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Loader2, Upload, CheckCircle2 } from "lucide-react";
import { compressImage } from "@/utils/imageCompression";

/**
 * VinPhotoScanner – kleiner Icon-Button, der ein Foto einer VIN-Plakette
 * aufnimmt (oder hochladen lässt) und via Backend-OCR die 17-stellige VIN zurückliefert.
 * Funktioniert auch bei rein gedruckten/geprägten VINs (kein Barcode nötig).
 */
const VinPhotoScanner = ({ onResult, buttonVariant = "outline", buttonSize = "icon" }) => {
  const [open, setOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setSubmitting(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 1600, quality: 0.85 });
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(compressed);
      });
      const res = await api.post("/api/vehicles/scan-vin", {
        image_base64: base64,
        ocr_type: "vin",
      }, { timeout: 60000 });
      if (res.data.success && res.data.vin) {
        onResult?.(res.data.vin);
        toast.success(`VIN erkannt: ${res.data.vin}`);
        setOpen(false);
      } else {
        toast.error(res.data.error || "VIN konnte nicht erkannt werden");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim VIN-Scan");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        onClick={(e) => { e.stopPropagation(); setOpen(true); setPreviewUrl(null); }}
        title="VIN per Foto erkennen (OCR)"
        data-testid="vin-photo-btn"
      >
        <Camera className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="vin-photo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" /> VIN per Foto erkennen
            </DialogTitle>
            <DialogDescription>
              Mach ein scharfes Foto von der VIN-Plakette (Windschutzscheibe, B-Säule, Motorraum oder Fahrzeugschein). Die 17-stellige Nummer wird automatisch ausgelesen.
            </DialogDescription>
          </DialogHeader>

          {submitting ? (
            <div className="flex flex-col items-center gap-3 py-6">
              {previewUrl && <img src={previewUrl} alt="VIN" className="max-h-48 rounded object-contain" />}
              <div className="flex items-center gap-2 text-primary">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="font-medium">VIN wird ausgelesen...</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-2">
              <p className="text-sm text-muted-foreground text-center">Foto aufnehmen oder aus Galerie wählen</p>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="gap-2 flex-1"
                  data-testid="vin-camera-btn"
                >
                  <Camera className="w-4 h-4" /> Foto aufnehmen
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => galleryRef.current?.click()}
                  className="gap-2 flex-1"
                  data-testid="vin-gallery-btn"
                >
                  <Upload className="w-4 h-4" /> Aus Galerie
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center max-w-md">
                <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-600" />
                Funktioniert auch ohne Barcode – die VIN wird per OCR aus dem Text gelesen.
              </p>
            </div>
          )}

          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <input
            ref={galleryRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VinPhotoScanner;
